import React from 'react';
import { Sun, Zap, Battery, Leaf, ChevronRight, Check, Award, TrendingUp, Shield, Users, MapPin, Calendar } from 'lucide-react';

const Solar = () => {
  const solarSolutions = [
    {
      icon: Sun,
      title: 'Solar PV Systems',
      description: 'Grid-tied and off-grid solar photovoltaic systems for residential, commercial, and industrial applications.',
      specs: '1kW - 1MW+',
      features: ['High-efficiency panels', 'Smart monitoring', '25-year warranty'],
      image: '/images/solutions/solar-installation.jpg'
    },
    {
      icon: Battery,
      title: 'Solar Storage Solutions',
      description: 'Advanced battery storage systems for energy independence and backup power during grid outages.',
      specs: '5kWh - 1MWh+',
      features: ['Lithium-ion technology', 'Scalable design', '10-year warranty'],
      image: '/images/products/ups-systems.jpg'
    },
    {
      icon: Zap,
      title: 'Solar Hybrid Systems',
      description: 'Hybrid solar systems combining PV, storage, and backup generators for maximum reliability and efficiency.',
      specs: 'Grid & Off-Grid',
      features: ['Seamless transition', 'Fuel savings', 'Remote monitoring'],
      image: '/images/products/generators.jpg'
    },
    {
      icon: Leaf,
      title: 'Solar Water Heating',
      description: 'Efficient solar thermal systems for commercial and residential water heating applications.',
      specs: '200L - 10,000L',
      features: ['Low maintenance', 'Energy savings', 'Rapid ROI'],
      image: '/images/products/solar-panels.jpg'
    },
    {
      icon: Shield,
      title: 'Solar Pumping Systems',
      description: 'Solar-powered water pumping solutions for agriculture, livestock, and remote communities.',
      specs: '0.5kW - 50kW',
      features: ['Off-grid operation', 'Low maintenance', 'Reliable water supply'],
      image: '/images/solutions/mv-switchgear.jpg'
    },
    {
      icon: TrendingUp,
      title: 'Solar Monitoring & Maintenance',
      description: 'Comprehensive monitoring and maintenance services to maximize system performance and longevity.',
      specs: '24/7 Monitoring',
      features: ['Real-time data', 'Predictive maintenance', 'Performance reports'],
      image: '/images/solutions/vfd-installation.jpg'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Cost Savings',
      description: 'Reduce electricity bills by up to 70% with solar energy'
    },
    {
      icon: Leaf,
      title: 'Environmental Impact',
      description: 'Reduce carbon footprint by 30-50 tons per year per system'
    },
    {
      icon: Award,
      title: 'Energy Independence',
      description: 'Protection against rising grid electricity costs'
    },
    {
      icon: Users,
      title: 'Government Support',
      description: 'Access to renewable energy incentives and tax benefits'
    }
  ];

  const solarProjects = [
    {
      title: 'Commercial Solar Installation',
      location: 'Upper Hill, Nairobi',
      image: '/images/projects/project-commercial.jpg',
      year: '2024',
      scope: '500kW Grid-Tied System',
      savings: '40% Energy Savings'
    },
    {
      title: 'Residential Solar System',
      location: 'Westlands, Nairobi',
      image: '/images/projects/project-residential.jpg',
      year: '2024',
      scope: '5kW with Battery Storage',
      savings: '90% Energy Independence'
    },
    {
      title: 'Industrial Solar Farm',
      location: 'Athi River, Kenya',
      image: '/images/projects/project-solar-farm.jpg',
      year: '2023',
      scope: '2MW Ground-Mounted',
      savings: '60% Energy Savings'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#0a3d4a] to-[#006D77] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff20_1px,transparent_1px),linear-gradient(to_bottom,#ffffff20_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1">
              <span className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sun size={18} className="mr-2" />
                Renewable Energy Solutions
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Powering Africa's
                <span className="text-yellow-300 block">Sustainable Future</span>
              </h1>
              <p className="text-lg text-white/80 max-w-lg mb-8">
                Comprehensive solar and renewable energy solutions for residential, commercial, and industrial applications across Kenya and East Africa.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="bg-yellow-300 text-[#006D77] px-8 py-3.5 rounded-full font-semibold hover:bg-yellow-400 transition-all shadow-lg flex items-center gap-2">
                  Get Free Consultation
                  <ChevronRight size={18} />
                </button>
                <button className="border-2 border-white/30 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/10 transition-all">
                  View Projects
                </button>
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-yellow-300/20 to-[#006D77]/20 rounded-2xl blur-3xl"></div>
                <img 
                  src="/images/hero/hero-panel-building.jpg"
                  alt="Solar Energy Solutions"
                  className="relative rounded-2xl shadow-2xl w-full h-80 object-cover"
                  onError={(e) => {
                    e.target.src = 'https://picsum.photos/seed/solarhero/1200/800';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-[#006D77] font-semibold text-sm uppercase tracking-wider inline-block bg-[#006D77]/10 px-4 py-1.5 rounded-full">
              Why Choose Solar
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4">
              Benefits of Renewable Energy
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="w-14 h-14 bg-[#006D77]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-[#006D77]" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solar Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#006D77] font-semibold text-sm uppercase tracking-wider inline-block bg-[#006D77]/10 px-4 py-1.5 rounded-full">
              Our Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4">
              Solar & Renewable Energy Solutions
            </h2>
            <p className="text-gray-600 text-lg mt-4">
              Comprehensive solar solutions designed for African conditions and requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solarSolutions.map((solution, idx) => {
              const Icon = solution.icon;
              return (
                <div key={idx} className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-2">
                  <div className="h-52 overflow-hidden relative">
                    <img 
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        e.target.src = `https://picsum.photos/seed/solar${idx}/800/600`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                    <div className="absolute top-4 right-4 bg-[#006D77] text-white px-3 py-1.5 rounded-full text-xs font-medium">
                      {solution.specs}
                    </div>
                    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-2.5 rounded-xl">
                      <Icon className="w-6 h-6 text-[#006D77]" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{solution.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{solution.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {solution.features.map((feature, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full flex items-center gap-1">
                          <Check size={12} className="text-[#006D77]" />
                          {feature}
                        </span>
                      ))}
                    </div>
                    <a 
                      href={`/solutions/${solution.title.toLowerCase().replace(/ & /g, '-').replace(/\s+/g, '-')}`}
                      className="inline-flex items-center text-[#006D77] font-medium hover:gap-2 transition-all"
                    >
                      Learn More
                      <ChevronRight size={16} className="ml-1" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solar Projects Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#006D77] font-semibold text-sm uppercase tracking-wider inline-block bg-[#006D77]/10 px-4 py-1.5 rounded-full">
              Our Work
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4">
              Solar Projects in Kenya
            </h2>
            <p className="text-gray-600 text-lg mt-4">
              Real-world solar installations delivering clean energy across the country.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {solarProjects.map((project, idx) => (
              <div key={idx} className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-2">
                <div className="h-56 overflow-hidden relative">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    onError={(e) => {
                      e.target.src = `https://picsum.photos/seed/solarproj${idx}/800/600`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                  <div className="absolute top-3 right-3 bg-yellow-300 text-[#006D77] px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5">
                    <Calendar size={12} />
                    {project.year}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="flex items-center text-xs text-gray-300 mb-1.5">
                      <MapPin size={14} className="mr-1.5" />
                      {project.location}
                    </div>
                    <h3 className="text-lg font-bold mb-1">{project.title}</h3>
                    <p className="text-sm text-gray-300">{project.scope}</p>
                    <div className="mt-2 inline-block bg-green-500/80 text-white text-xs px-3 py-1 rounded-full">
                      {project.savings}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a 
              href="/projects" 
              className="inline-block border-2 border-[#006D77] text-[#006D77] px-8 py-3 rounded-full font-semibold hover:bg-[#006D77] hover:text-white transition-all"
            >
              View All Projects
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-[#0a3d4a] to-[#006D77] text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Go Solar?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Get a free consultation and quote for your solar project. Our experts will design the perfect solution for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-yellow-300 text-[#006D77] px-8 py-3.5 rounded-full font-semibold hover:bg-yellow-400 transition-all shadow-lg">
              Request Free Quote
            </button>
            <a 
              href="tel:+254735338408" 
              className="border-2 border-white/30 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/10 transition-all"
            >
              Call Now: 0735 338 408
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-[#006D77] font-semibold text-sm uppercase tracking-wider inline-block bg-[#006D77]/10 px-4 py-1.5 rounded-full">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4">
              Common Questions About Solar
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: 'How much does a solar system cost?',
                a: 'Solar system costs vary based on size and requirements. A typical residential system (3-5kW) costs between KES 300,000 - 600,000, while commercial systems (50kW+) require custom quotes.'
              },
              {
                q: 'How long do solar panels last?',
                a: 'Quality solar panels come with 25-year performance warranties and typically last 25-30 years with minimal degradation (0.5-1% per year).'
              },
              {
                q: 'Can I go completely off-grid with solar?',
                a: 'Yes, with proper battery storage sizing. We design systems for both grid-tied and off-grid applications depending on your energy needs and budget.'
              },
              {
                q: 'What maintenance do solar systems need?',
                a: 'Solar systems require minimal maintenance - periodic cleaning of panels (2-4 times per year) and annual system checks by professionals.'
              },
              {
                q: 'Do solar systems work during cloudy weather?',
                a: 'Yes, modern solar panels still produce electricity on cloudy days (at 20-30% capacity). Battery storage ensures you have power during extended cloudy periods.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solar;
